import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { coupleFeedbackDtoSchema, coupleFeedbackForEventDtoSchema, coupleFeedbackListDtoSchema, coupleFeedbackPageDtoSchema, reviewLinkSuggestionsDtoSchema, venueReviewLinksDtoSchema, type CoupleFeedbackDto, type CoupleFeedbackForEventDto, type CoupleFeedbackListDto, type CoupleFeedbackPageDto, type ReviewLinkSuggestionsDto, type VenueReviewLinksDto } from '@lib/dto/couple-feedback.dto'
import type { CoupleFeedbackClickInput, PutVenueReviewLinksInput, SubmitCoupleFeedbackInput, UpdateCoupleFeedbackInput } from '@lib/input/couple-feedback.input'
import type { ICoupleFeedbackService } from '@/types/CoupleFeedbackService.types'

class CoupleFeedbackService implements ICoupleFeedbackService {
  async links(venueId: string): Promise<VenueReviewLinksDto> {
    try {
      return await api.get(`/venues/${venueId}/review-links`, venueReviewLinksDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load review links', { venueId }, error)
      throw error
    }
  }

  async replaceLinks(venueId: string, input: PutVenueReviewLinksInput): Promise<VenueReviewLinksDto> {
    try {
      return await api.put(`/venues/${venueId}/review-links`, input, venueReviewLinksDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save review links', { venueId }, error)
      throw error
    }
  }

  async suggestions(venueId: string): Promise<ReviewLinkSuggestionsDto> {
    try {
      return await api.get(`/venues/${venueId}/review-links/suggestions`, reviewLinkSuggestionsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load review place suggestions', { venueId }, error)
      throw error
    }
  }

  async page(eventId: string): Promise<CoupleFeedbackPageDto> {
    try {
      return await api.get(`/events/${eventId}/feedback`, coupleFeedbackPageDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load feedback', { eventId }, error)
      throw error
    }
  }

  async submit(eventId: string, input: SubmitCoupleFeedbackInput): Promise<CoupleFeedbackPageDto> {
    try {
      return await api.post(`/events/${eventId}/feedback`, input, coupleFeedbackPageDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to send feedback', { eventId }, error)
      throw error
    }
  }

  async click(eventId: string, input: CoupleFeedbackClickInput): Promise<CoupleFeedbackPageDto> {
    try {
      return await api.post(`/events/${eventId}/feedback/clicks`, input, coupleFeedbackPageDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to record a review link', { eventId }, error)
      throw error
    }
  }

  async forEvent(eventId: string): Promise<CoupleFeedbackForEventDto> {
    try {
      return await api.get(`/events/${eventId}/couple-feedback`, coupleFeedbackForEventDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load feedback', { eventId }, error)
      throw error
    }
  }

  async list(): Promise<CoupleFeedbackListDto> {
    try {
      return await api.get('/feedback', coupleFeedbackListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load feedback', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<CoupleFeedbackDto> {
    try {
      return await api.get(`/feedback/${id}`, coupleFeedbackDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load feedback', { id }, error)
      throw error
    }
  }

  async update(id: string, input: UpdateCoupleFeedbackInput): Promise<CoupleFeedbackDto> {
    try {
      return await api.patch(`/feedback/${id}`, input, coupleFeedbackDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update feedback', { id }, error)
      throw error
    }
  }
}

export const coupleFeedbackService = new CoupleFeedbackService()
